import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Filter, ValidationMessage} from '@github-ui/filter'
import {
  defaultFilterConfig,
  type FilterProvider,
  type FilterQuery,
  isNestedFilterProvider,
  SubmitEvent,
} from '@github-ui/filter-core'
import {IssueFieldFilterQueryParser, useFilterProviders, useOrgAwareProviders} from '@github-ui/filter-providers-issues'
import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {searchUrl} from '@github-ui/issue-url-helper'
import {HOTKEYS} from '@github-ui/issue-viewer/Hotkeys'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useLocation, useParams} from '@github-ui/react-router'
import {useAnalytics} from '@github-ui/use-analytics'
import {ModifierKeys, useKeyPress} from '@github-ui/use-key-press'
import {FormControl} from '@primer/react'
import {type PropsWithChildren, type ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {MESSAGES} from '../../constants/messages'
import {
  CUSTOM_VIEW,
  DEFAULT_QUERY,
  EMPTY_VIEW,
  KNOWN_VIEWS,
  LABEL_BASE_QUERY,
  REPOSITORY_VIEW,
} from '../../constants/view-constants'
import {
  useDirtySearchQuery,
  useQueryContext,
  useQueryEditState,
  useQueryFocus,
  useSetActiveSearchQuery,
  useSetCurrentPage,
  useSetDirtySearchQuery,
} from '../../contexts/QueryContext'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import type {AppPayload} from '../../types/app-payload'
import {extractSsoOrgsFromFilterQuery, filterSsoValidationMessages} from '../../utils/extract-sso-orgs'
import {
  isCreatedByAppIssuePathForRepo,
  isCustomViewIssuePathForRepo,
  isLabelShowPathForRepo,
  isNewIssuePath,
  removeInvalidFiltersFromQuery,
  replaceTokensDifferentially,
} from '../../utils/urls'
import type {SearchBarCurrentViewFragment$key} from './__generated__/SearchBarCurrentViewFragment.graphql'
import type {SearchBarRepo$key} from './__generated__/SearchBarRepo.graphql'
import styles from './SearchBar.module.css'
import {SearchBarActions} from './SearchBarActions'

type SearchBarProps = {
  currentViewKey: SearchBarCurrentViewFragment$key
  currentRepository: SearchBarRepo$key | null
  queryFromCustomView?: string | null
  onSsoOrgsFromFilter?: (orgs: string[]) => void
  ssoOrgNames?: string[]
}

const DynamicWrapper = ({editing, children}: {editing: boolean; children: ReactNode}) => {
  return editing ? <FormControl className={styles.FormControl}>{children}</FormControl> : <>{children}</>
}

let shouldRefocusFilter = false

/**
 * Determines the query string from the view ID
 */
function getQueryFromViewId(pathname: string, viewQuery: string, viewId: string): string | undefined {
  if (isCustomViewIssuePathForRepo(pathname) || isLabelShowPathForRepo(pathname)) {
    return viewQuery
  }

  const defaultViewQuery = [...KNOWN_VIEWS, EMPTY_VIEW].find(v => v.id === viewId)?.query
  if (defaultViewQuery) {
    // Inserts a space after the default query to make it easier to append additional filters
    // This is consistent with the behaviour of global search
    return `${defaultViewQuery} `
  }

  return undefined
}

const EMPTY_SSO_ORG_NAMES: string[] = []

export function SearchBar({
  currentViewKey,
  currentRepository,
  queryFromCustomView,
  onSsoOrgsFromFilter,
  ssoOrgNames = EMPTY_SSO_ORG_NAMES,
  children,
}: PropsWithChildren<SearchBarProps>) {
  const ref = useRef<HTMLInputElement>(null)

  const {isCustomView} = useQueryContext()
  const setActiveSearchQuery = useSetActiveSearchQuery()
  const setCurrentPage = useSetCurrentPage()
  const {isEditing} = useQueryEditState()

  const [validationMessage, setValidationMessage] = useState<string[]>([])

  const {search, pathname} = useLocation()

  const urlSearchParams = new URLSearchParams(search)
  const urlQuery = urlSearchParams.get('q')

  const {scoped_repository} = useAppPayload<AppPayload>()
  const repositoryScope = scoped_repository ? `${scoped_repository.owner}/${scoped_repository.name}` : undefined

  const {navigateToUrl} = useAppNavigate()
  const issue_fields_global_search_enabled = isFeatureEnabled('issue_fields_global_search')

  // GenericView is a union type between team and user search shortcut
  // Adding this here resulted in possible null values for the returned props
  // It should not be the case
  const {
    id: viewId,
    scopingRepository,
    query,
  } = useFragment<SearchBarCurrentViewFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchBarCurrentViewFragment on Shortcutable {
        id
        name
        query
        scopingRepository {
          name
          owner {
            login
          }
        }
      }
    `,
    currentViewKey,
  )

  const currentRepositoryData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchBarRepo on Repository {
        isInOrganization
        isOwnerEnterpriseManaged
        nameWithOwner
        viewerCanPush
        viewerCanSeeIssueFields
        isWritable
      }
    `,
    currentRepository,
  )

  // This is legacy behavior found in repos/issues search
  const {author, assignee, mentioned, label} = useParams<{
    author: string
    assignee: string
    mentioned: string
    label: string
  }>()
  const customViewQuery = `${CUSTOM_VIEW.defaultQuery} ${CUSTOM_VIEW.query({
    author,
    assignee,
    mentioned,
    label,
    createdByApp: isCreatedByAppIssuePathForRepo(pathname),
  })}`
  let viewQuery = query

  if (isCustomViewIssuePathForRepo(pathname)) {
    viewQuery = customViewQuery
  } else if (isLabelShowPathForRepo(pathname) && label) {
    viewQuery = replaceTokensDifferentially(LABEL_BASE_QUERY, [label], 'label')
  }

  const originalQuery = scopingRepository
    ? `repo:${scopingRepository.owner.login}/${scopingRepository.name} ${viewQuery}`
    : viewQuery

  const dirtySearchQuery = useDirtySearchQuery()
  const setDirtySearchQuery = useSetDirtySearchQuery()
  const {setShouldFocusSearchOnNav, shouldFocusSearchOnNav} = useQueryFocus()

  const setCurrentQuery = useCallback(
    (currentQuery: string) => {
      setActiveSearchQuery(currentQuery)
      setDirtySearchQuery(null)
    },
    [setActiveSearchQuery, setDirtySearchQuery],
  )

  useEffect(() => {
    if (!isNewIssuePath(pathname)) {
      const currentQuery = urlQuery || originalQuery || ''
      setActiveSearchQuery(currentQuery)
      setDirtySearchQuery(urlQuery)
    }
  }, [setCurrentQuery, urlQuery, pathname, originalQuery, setActiveSearchQuery, setDirtySearchQuery])

  const {sendAnalyticsEvent} = useAnalytics()

  const filterProviders = useFilterProviders({
    repositoryScope,
    isOrgScope: !!currentRepositoryData?.isInOrganization,
    isIssueFieldsEnabled:
      isFeatureEnabled('issue_fields_visibility_settings') || !!currentRepositoryData?.viewerCanSeeIssueFields,
    isIssueFieldsGlobalSearchEnabled: issue_fields_global_search_enabled,
  })

  const onSubmit = useCallback(
    (request: FilterQuery, event: SubmitEvent) => {
      let searchQuery = request.raw
      if (event === SubmitEvent.Clear) {
        searchQuery = originalQuery
        setCurrentQuery(originalQuery)
      }

      // Here we first remove white spaces from both ends, then we replace consecutive whitespaces
      // in the middle with a singular whitespace
      // e.g. "  is:issue     state:open   " becomes "is:issue state:open"
      let finalQuery = (searchQuery ? searchQuery : DEFAULT_QUERY).trim().replace(/\s+/g, ' ')

      const filterConfig = request.config
      // when the filter is using the parser v2 that supports the advanced search grammar,
      // the query is already validated by this point and incompatible with the following sanitization
      if (!filterConfig.groupAndKeywordSupport) {
        finalQuery = removeInvalidFiltersFromQuery(searchQuery)
      }
      sendAnalyticsEvent('search.execute', 'FILTER_BAR_INPUT', {new_query: finalQuery})

      const url = searchUrl({viewId, query: finalQuery})

      shouldRefocusFilter = true

      navigateToUrl(url, {preventAutofocus: true})
      setCurrentPage(1)
    },
    [sendAnalyticsEvent, navigateToUrl, setCurrentPage, originalQuery, setCurrentQuery, viewId],
  )

  const singleKeyShortcutIsEnabled = useMemo(() => areCharacterKeyShortcutsEnabled(), [])

  const onShortcutKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (!singleKeyShortcutIsEnabled) {
        return
      }

      if (ref && ref.current) {
        ref.current.focus()
        const queryLength = ref.current.value?.length || 0
        ref.current.setSelectionRange(queryLength, queryLength)
        e.preventDefault()
      }
    },
    [singleKeyShortcutIsEnabled],
  )

  const onInputChange = useCallback(
    (filter: string) => {
      setDirtySearchQuery(filter)
    },
    [setDirtySearchQuery],
  )

  const onValidation = useCallback(
    (messages: string[], filterQuery: FilterQuery) => {
      setValidationMessage(filterSsoValidationMessages(messages, ssoOrgNames))
      onSsoOrgsFromFilter?.(extractSsoOrgsFromFilterQuery(filterQuery))
    },
    [setValidationMessage, onSsoOrgsFromFilter, ssoOrgNames],
  )

  // Restore focus to the filter input after submit
  useEffect(() => {
    if (shouldRefocusFilter && ref.current) {
      shouldRefocusFilter = false
      ref.current.focus()
    }
  }, [pathname, search, urlQuery])

  useKeyPress([HOTKEYS.focusSearch], onShortcutKeyPress, {[ModifierKeys.metaKey]: true})
  useKeyPress([HOTKEYS.focusSearch], onShortcutKeyPress, {[ModifierKeys.ctrlKey]: true})

  // We can only get into a valid edit state if details are not being shown for a custom view header
  const editingContent = isEditing && isCustomView(viewId)

  useEffect(() => {
    if (!ref.current || !editingContent) return

    const input = ref.current

    function handleFocus() {
      setShouldFocusSearchOnNav(true)
    }

    input.addEventListener('focus', handleFocus)

    return () => input.removeEventListener('focus', handleFocus)
  }, [editingContent, setShouldFocusSearchOnNav])

  useEffect(() => {
    if (editingContent && ref.current && shouldFocusSearchOnNav) {
      requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.focus()
          const queryLength = ref.current.value?.length || 0
          ref.current.setSelectionRange(queryLength, queryLength)
        }
      })
    }
  }, [editingContent, shouldFocusSearchOnNav])

  const queryFromViewId = getQueryFromViewId(pathname, viewQuery, viewId)

  // We give precedence to the query from the URL over the query from the viewId
  // This prevents flickering when the page loads with a URL query that differs from the view's default query
  const effectiveQueryFromViewId = urlQuery ?? queryFromViewId

  const effectiveQuery = useMemo(() => {
    return dirtySearchQuery ?? effectiveQueryFromViewId ?? queryFromCustomView ?? ''
  }, [dirtySearchQuery, effectiveQueryFromViewId, queryFromCustomView])

  let showClearButton = false

  // This should be reworked along with the many different variables we have that represent _some_ type of query input
  // For now, this ensures the clear button works correctly in all surfaces areas, but ideally, search bar shouldn't
  // be concerned with almost _any_ of these special cases.
  if (isCustomView(viewId)) {
    // Issues Dashboard Custom (Saved) View - i.e. github.com/issues
    showClearButton = dirtySearchQuery?.trim() !== queryFromCustomView?.trim()
  } else if (viewId === REPOSITORY_VIEW.id) {
    // Repository / Issues Search - i.e. github.com/github/issues
    showClearButton = dirtySearchQuery?.trim() !== queryFromViewId?.trim()
  } else {
    // Issues Dashboard Known (Custom) View - i.e. github.com/issues/assigned
    showClearButton = dirtySearchQuery?.trim() !== effectiveQueryFromViewId?.trim()
  }

  const providers = useOrgAwareProviders({
    baseFilterProviders: filterProviders,
    effectiveQuery,
    isIssueFieldsGlobalSearchEnabled: issue_fields_global_search_enabled,
  })

  const customParser = useMemo(() => {
    const filteredProviders = providers.filter(p => !isNestedFilterProvider(p)) as FilterProvider[]
    const config = {
      ...defaultFilterConfig,
      aliasMatching: true,
      groupAndKeywordSupport: true,
    }
    return new IssueFieldFilterQueryParser(filteredProviders, config)
  }, [providers])

  return (
    <DynamicWrapper editing={editingContent}>
      {/* eslint-disable-next-line primer-react/direct-slot-children */}
      {editingContent && <FormControl.Label visuallyHidden>{MESSAGES.query}</FormControl.Label>}
      <div className={`${styles.gap8} px-0 ${editingContent ? 'd-flex' : 'd-block'} flex-row flex-justify-between`}>
        <div className={`${styles.filterContainer} ${styles.gap8} d-flex flex-row flex-1 flexWrap min-width-0`}>
          <div className={`${styles.filter} d-flex flex-1 flex-column`}>
            <Filter
              id={viewId ?? 'search'}
              context={repositoryScope ? {repo: repositoryScope} : undefined}
              label={editingContent ? LABELS.issueEditingSearchInputAriaLabel : LABELS.issueSearchInputAriaLabel}
              visuallyHideLabel={!editingContent}
              placeholder={LABELS.issueSearchInputPlaceholder}
              onSubmit={onSubmit}
              onChange={onInputChange}
              providers={repositoryScope ? filterProviders : providers}
              inputRef={ref}
              filterValue={effectiveQuery}
              variant={'input'}
              settings={{aliasMatching: true, groupAndKeywordSupport: true}}
              showValidationMessage={false}
              onValidation={onValidation}
              showClearButton={dirtySearchQuery !== null && showClearButton}
              customParser={repositoryScope ? undefined : customParser}
            />
          </div>

          {children}

          {scoped_repository && !isNewIssuePath(pathname) && (
            <SearchBarActions
              isOwnerEnterpriseManaged={currentRepositoryData?.isOwnerEnterpriseManaged ?? false}
              nameWithOwner={currentRepositoryData?.nameWithOwner ?? null}
              isWritable={currentRepositoryData?.isWritable ?? null}
            />
          )}

          {validationMessage.length > 0 && (
            <div className={`${styles.validation} mt-1`}>
              <ValidationMessage messages={validationMessage} id="repository-validation-message" />
            </div>
          )}
        </div>
      </div>
    </DynamicWrapper>
  )
}
